import { gsap } from 'gsap'
import {
	motionProps as botMotionProps,
	getBotMessage,
	getBotIcon,
} from '@/components/navigation/Bot/Bot.motion'
import { BotInstance } from '@/components/navigation/Bot/Bot.instance'

function toArray(value) {
	return [...(value ?? [])].filter(Boolean)
}

export function createMenuMotion(ctx, { menu, background, persistentBar, getPage }) {
	const botController = ctx.bot ?? BotInstance
	const { pages, dividers } = menu
	const { bot, pageIndicator } = persistentBar

	let page = null
	let timeline = null

	function killTweens() {
		if (timeline) {
			timeline.kill()
			timeline = null
		}

		gsap.killTweensOf([
			persistentBar.main,
			background,
			menu.content,
			...toArray(dividers.horizontal),
			...toArray(dividers.vertical),
			...toArray(pages.labels),
		])
	}

	// resolves when persistent bar is no longer visible
	function open({ immediate } = {}) {
		return new Promise((resolve) => {
			page = getPage()
			killTweens()

			timeline = gsap.timeline({
				ease: 'none',
				paused: true,
				onComplete: () => {
					timeline = null
					resolve()
				},
			})

			timeline.addLabel('persistent-bar-main', 0)
			timeline.to([bot, pageIndicator], { duration: 0.1, opacity: 0 }, 'persistent-bar-main')

			timeline.addLabel('background', 0)
			const backgroundEase = 'm3.emphasized'
			timeline.to(
				background,
				{ duration: 0.65, ease: backgroundEase, height: '100%' },
				'background',
			)
			timeline.to(menu.content, { duration: 0.65, ease: backgroundEase, yPercent: 0 }, 'background')
			timeline.to(
				menu.content,
				{ duration: 0.23, delay: 0.03, ease: 'power3.in', opacity: 1 },
				'background',
			)

			timeline.addLabel('dividers', 0.1)
			timeline.fromTo(
				dividers.horizontal,
				{ scaleX: 0 },
				{ duration: 2, ease: 'expo', stagger: 0.1, transformOrigin: 'left', scaleX: 1 },
				'dividers',
			)
			timeline.fromTo(
				dividers.vertical,
				{ scaleY: 0 },
				{ duration: 2, delay: 0.4, ease: 'expo', stagger: 0.1, transformOrigin: 'top', scaleY: 1 },
				'dividers',
			)

			timeline.addLabel('pages', 0.3)
			timeline.fromTo(
				pages.labels,
				{ yPercent: 110 },
				{ duration: 0.6, stagger: 0.05, ease: 'power3', yPercent: 0 },
				'pages',
			)

			// avoids cursor flicker as translated pages are passing through pointer
			timeline.set(menu.clickables, { pointerEvents: 'none' }, 0)
			timeline.set(menu.clickables, { clearProps: 'pointerEvents' }, 'dividers+=0.2')

			if (immediate) {
				if (page?.blur) {
					page.blur.progress = 1
				}

				timeline.progress(1)
				timeline?.kill()
			} else {
				timeline.play()

				if (page?.blur) {
					page.blur.enable({ duration: 0.6, delay: 0.05, ease: 'm3.standard' })
				}
			}
		})
	}

	// resolves when persistent bar becomes visible
	function close(_options = {}) {
		const { immediate = false, focusPage = true } = _options

		return new Promise((resolve) => {
			killTweens()

			timeline = gsap.timeline({
				ease: 'none',
				paused: true,
				onComplete: () => {
					timeline = null
				},
			})

			timeline.addLabel('persistent-bar-main', 0.3)
			timeline.add(() => resolve(), 'persistent-bar-main+=0.2')

			timeline.set([bot, pageIndicator], { duration: 0.1, opacity: 1 }, 'persistent-bar-main')

			if (!immediate) {
				timeline.add(() => {
					const botIcon = getBotIcon('active')
					const botMessage = getBotMessage('active')

					if (!botIcon || !botMessage.length) return

					gsap.killTweensOf([botIcon, ...botMessage])

					const animationPromise = new Promise((resolve) => {
						gsap.fromTo(botIcon, { yPercent: -101 }, { ...botMotionProps.icon.enter, yPercent: 0 })
						gsap.fromTo(
							botMessage,
							{ yPercent: 101 },
							{ ...botMotionProps.message.enter, yPercent: 0, onComplete: resolve },
						)
					})

					botController.component.deferTransition?.(animationPromise)
				}, 'persistent-bar-main')
			}

			const pageIndicatorBackground = pageIndicator.querySelector('.background')
			const pageIndicatorLabel = pageIndicator.querySelector('.label .m-t')
			if (pageIndicatorBackground && pageIndicatorLabel) {
				timeline.fromTo(
					pageIndicatorBackground,
					{ xPercent: 110 },
					{ duration: 1, ease: 'expo.out', xPercent: 0 },
					'persistent-bar-main',
				)
				timeline.fromTo(
					pageIndicatorLabel,
					{ yPercent: 110 },
					{ duration: 0.55, delay: 0.225, ease: 'power3', yPercent: 0 },
					'persistent-bar-main',
				)
			}

			timeline.addLabel('background', 0)
			timeline.to(
				background,
				{ duration: 0.52, ease: 'power3.inOut', height: persistentBar.container.offsetHeight },
				'background',
			)
			timeline.to(
				menu.content,
				{ duration: 0.49, ease: 'power3.inOut', yPercent: 100 },
				'background',
			)
			timeline.to(menu.content, { duration: 0.3, ease: 'power3.inOut', opacity: 0 }, 'background')

			timeline.set(background, { height: null })

			if (immediate) {
				if (page?.blur && focusPage) {
					page.blur.progress = 0
				}

				timeline.progress(1)
				timeline?.kill()
			} else {
				timeline.play()

				if (page?.blur && focusPage) {
					if (!page.loader?.isDestroyed) {
						page.blur.disable({ duration: 0.52, ease: 'power3.inOut' })
					}
				}
			}

			page = null
		})
	}

	function destroy() {
		killTweens()
	}

	return { open, close, destroy }
}

export function createNavbarMotion(ctx, elements) {
	const { container } = elements
	container.removeAttribute('data-motion-fallback')

	const menu = createMenuMotion(ctx, elements)

	function destroy() {
		container.setAttribute('data-motion-fallback', '')

		menu.destroy()
	}

	return { menu, destroy }
}
